import { CustomState } from "@affinity/Store/Reducers";
import { customDispatch } from "@affinity/Store/ServiceBase";
import { AffinityDelivery, AffinityProduct } from "@affinity/Store/StoreTypes";
import { defaultProductPricingDTO, defaultShipToCollectionModel, defaultShipToModel } from "@affinity/Store/StoreTypeStates";
import { createHandlerChainRunner, Handler, HasOnComplete, makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { AddCartLinesApiParameter, Cart, getCart, updateCart } from "@insite/client-framework/Services/CartService";
import { getBillTo, updateBillTo, UpdateBillToApiParameter } from "@insite/client-framework/Services/CustomersService";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadDataIfNeeded";
import { BillToModel, CartLineCollectionModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import cloneDeep from "lodash/cloneDeep";

// External parameters
interface PlaceOrderPaymentStartParameters {
    customState: CustomState,
    cartId: string | undefined,
}

// Internal parameters
interface Props {
    isAuthenticated: boolean,
    billToId: string | undefined,
    billTo: BillToModel | undefined,
    billToResult: BillToModel | undefined,
    cartId: string | undefined,
    cart: Cart | undefined,
    error: any,
}

type Parameter = PlaceOrderPaymentStartParameters & HasOnComplete<Props>;
type HandlerType = Handler<Parameter, Props>;

const ClearCurrentCart: HandlerType = (props) => {
    props.dispatch(clearCurrentCart());
}

const VerifyUserState: HandlerType = (props) => {
    const state = props.parameter.customState;
    props.isAuthenticated = state.context.session.isAuthenticated;
    props.billToId = state.context.session.billToId;
}

const UpdateBillToNumber: HandlerType = async (props) => {
    const state = props.parameter.customState;
    const contactNumber = state.affinity_place_order.phone;

    if (props.billToId) {
        props.billTo = await getBillTo({ billToId: props.billToId })
    }

    if (props.billTo && props.billTo.phone && contactNumber) {
        const updatedBillTo = {
            ...props.billTo,
            phone: contactNumber,
        }

        const billToApiParameter: UpdateBillToApiParameter = {
            billTo: updatedBillTo
        }

        props.billToResult = await updateBillTo(billToApiParameter);
    }
}

const GetCart: HandlerType = async props => {
    try {
        props.cart = (await getCart({ cartId: props.parameter.cartId ?? 'current'}))?.cart;
        props.cartId = props.cart.id;
    } catch (error) {
        logger.error(`Order Completion Load Step - Failed to load card for ID: ${props.parameter.cartId}, ${error}`);
        props.error = error;
    }
};

const LoadCart: HandlerType = async (props) => {
    const awaitableLoadData = makeHandlerChainAwaitable(loadDataIfNeeded);
    await awaitableLoadData({
        cartId: props.cartId,
        cartLinesShouldBeExpanded: true,
    })(props.dispatch, props.getState);
}

const SetupCart: HandlerType = async (props) => {
        // Temp state references till tidy up legacy code
        const state = props.parameter.customState;
        const cartId = props.cartId;
        const cartModel = props.cart;
        const selectedProduct = state.affinity_place_order.selectedProduct === AffinityProduct.Premium 
            ? state.affinity_place_order.productPremium 
            : state.affinity_place_order.productRegular;
        const selectedDelivery = state.affinity_place_order.pricingSelected;
        const quantity = state.affinity_place_order.quantity;
        const customerNumber = state.affinity_place_order.shipToModel?.customerNumber;
        const shipToERP =  state.affinity_place_order.shipToModel?.customerSequence;
        const productERP = selectedProduct?.productNumber;
        const deliveryNotes = state.affinity_place_order.deliveryInstructions;
        const purchaseOrderValue = state.affinity_place_order.purchaseOrder;
        const shipTo = state.affinity_place_order.selectedAddress;

        if (!selectedProduct || !selectedDelivery || !cartModel)
        {
            return false;
        }
    
        // MOVE BELOW TO HANDLER
        // WITH STATE ENUM IN STATE checkout = inProgress | isComplete | undefined
        // IF inProgress show spinner, IF isComplete do redirect, first change step and set in progress to show spinner
        // Do stuff
        // End with isComplete / redirect URL to all in terminal step...

        // PO update via API only as normally a checkout not a cart process
        let poValue = purchaseOrderValue ?? '';
        if (poValue === ''){
            poValue = 'Not Provided';
        }
        const updatedCart: Cart = {
            ...cloneDeep(cartModel),
            poNumber: poValue,
            paymentMethod: cartModel.paymentOptions?.paymentMethods?.find(method => method.name === "CC"),
        };
        const updateCartResults = await updateCart({ cart: updatedCart });

        props.dispatch(setCurrentShipTo({
            shipToId: shipTo?.id ?? ''
        }));

        // TODO TEST REFACTOR REMOVING PRICING DTO LINK
        const adjustedPrice = defaultProductPricingDTO;
        adjustedPrice.unitRegularPrice = selectedDelivery?.unitPriceWithUpliftAndVAT;
        adjustedPrice.unitNetPrice = selectedDelivery?.unitPriceWithUplift;
        adjustedPrice.unitOfMeasure = "litres";
        adjustedPrice.productId = selectedProduct.id;

        // 1 TRY AddLinesToCart to add in a single step and set in stone?
        // 2 Extend pricing service in C# and debug / handle there  
        // TODO REFACTOR WITH DEFAULT HELPER AS PER OTHER STEPS
        const cartLinesDto: CartLineCollectionModel = {
            cartLines: [{
                    productId: selectedProduct.id,
                    productName: selectedProduct.productTitle, 
                    qtyOrdered: quantity!,
                    unitOfMeasure: 'litres',
                    unitOfMeasureDescription: 'litres',
                    unitOfMeasureDisplay: 'litres',
                    pricing: cloneDeep(adjustedPrice),

                    allowZeroPricing: false,
                    altText: '',
                    availability: null,
                    baseUnitOfMeasure: 'litres',
                    baseUnitOfMeasureDisplay: 'litres',
                    brand: null,
                    breakPrices: null,
                    canAddToCart: true,
                    canAddToWishlist: false,
                    canBackOrder: false,
                    costCode: '',
                    customerName: '',
                    erpNumber: '', // DO SOMETHING WITH THIS
                    hasInsufficientInventory: false,
                    id: '', // DO SOMETHING WITH THIS???
                    idx: undefined,
                    isActive: true,
                    isConfigured: true,
                    isDiscontinued: false,
                    isDiscounted: false,
                    isFixedConfiguration: true,
                    isPromotionItem: false,
                    isQtyAdjusted: false,
                    isRestricted: false,
                    isSubscription: false,
                    line: null,
                    manufacturerItem: '',
                    notes: deliveryNotes ?? '',
                    productSubscription: null,
                    productUri: '', // SORT THIS DO NOT WANT GETTIG BACK TO OOTB DETAIL PAGE (JUST FUEL PRODUCT PAGE?)
                    qtyLeft: 0,
                    qtyOnHand: quantity!,
                    qtyPerBaseUnitOfMeasure: 1,
                    quoteRequired: false,
                    requisitionId: null,
                    salePriceLabel: '',
                    sectionOptions: [], // 2023 11 Upgrade to 5.1.2309.6521 - Breaking Change
                    shortDescription: 'SHORT',
                    smallImagePath: '',
                    trackInventory: false,
                    uri: '',
                    properties: {
                        isFuelProduct: "true",
                        pricingInfo: JSON.stringify(selectedDelivery!), // Check matches Fuel MVP if needed
                        customerNumber: customerNumber!,
                        shipToERP: shipToERP!,
                        productERP: productERP!,
                        poNumber: poValue === 'Not Provided' ? '' : poValue,
                    },
                    cantBuy: false, // 2023 03 Upgrade to 5.1.2303.3265 - Breaking Change
                }
            ],
            notAllAddedToCart: true,
            pagination: null,
            uri: '',
            properties: {},
        };

        // OOTB Add To Cart (Not extended)
        const awaitableAddLinesToCart = makeHandlerChainAwaitable(addLinesToCart);
        const apiParameter: AddCartLinesApiParameter = {
            cartId: cartId!,
            cartLineCollection: cartLinesDto,
        }
        await awaitableAddLinesToCart({ apiParameter })(props.dispatch, props.getState);

        logger.info("Add to cart successful");
        // Need bill to / ship to set at ORDER level NOT line level now.
        // Same for both???

        // HandlingAmountOverride to zero out delivery? where? => on product???

        // Toast popup possible here or in final display step before redirect
        // See ProductAddToCartButton for example
        // //addFuelProductToBasket({ purchaseOrder: poValue});
}

const Tracking: HandlerType = props => {
    //
}

export const FireOnComplete: HandlerType = props => {
    props.parameter.onComplete?.(props);
};

const chain = [
    // UpdateBillToNumber, NOT REQUIRED BUT MAY BE NEEDED IN FUTURE
    ClearCurrentCart,
    VerifyUserState,
    GetCart,
    LoadCart,
    SetupCart,
    Tracking,
    FireOnComplete,
];

const addAddress = makeHandlerChainAwaitable(createHandlerChainRunner(chain, "PlaceOrderPaymentStart"));
export default addAddress;